/*
 * KM2 shared dependency guard — the ONE place that answers
 * "is my dependency here, and if not, WHY?"
 *
 * Why this exists
 * ---------------
 * Every KM2 module is a global defined by a deferred asset (window.KM2ProductModel from
 * km2-product-model.js, window.KM2Brands from mega-menu-brands.js, ...). Consumers checked for
 * those globals in one of two ways, and BOTH were wrong:
 *
 *   1. `console.error('[x] window.KM2ProductModel missing — ...')` — copy-pasted verbatim into
 *      SIX files. Correct behaviour, zero reuse: a seventh consumer had to remember to add it,
 *      and each copy could drift.
 *   2. `var p = (window.KM2Brands && window.KM2Brands.createProductPager) ? ... : null;`
 *      followed by `if (!p) return [];` — which fails SILENTLY. The rail renders nothing: no
 *      error, no empty state. It looks deliberate, so nobody investigates. This is exactly what
 *      hid a blank EBC rail — the pager factory was absent and the section quietly returned no
 *      products, for an unknown number of page loads.
 *
 * A missing shared global is never a data condition — it is a load-order or delivery fault.
 * It must be LOUD, and the message must carry enough state to diagnose it WITHOUT a rerun:
 * which global, which asset, whether the <script> tag was even in the DOM, and how far the
 * document had parsed. That is what get()/need() emit below.
 *
 * Load order: this loads BLOCKING and FIRST in layout/theme.liquid's <head> — deliberately NOT
 * deferred. Section inline scripts run at parse time, before any deferred asset, and they call
 * KM2Deps.ready() to schedule their init; a deferred guard would not exist yet for them. It is
 * tiny and data-only (no DOM writes, no network), so the parser cost is negligible. Callers still
 * use optional access (window.KM2Deps && ...) so a missing guard can never itself break a page.
 */
(function () {
  if (window.KM2Deps) return; // idempotent — single guard per page


  // globalName -> the asset expected to define it. Extend when a new shared global lands; an
  // unmapped global still reports, it just cannot name its asset.
  var ASSETS = {
    KM2ProductModel: 'km2-product-model.js',
    KM2ProductCard: 'km2-product-card.js',
    KM2Rail: 'km2-rail.js',
    KM2Brands: 'mega-menu-brands.js',
    KM2BrandTiles: 'km2-brand-tiles.js'
  };

  var reported = Object.create(null); // one report per context+subject — never a console flood
  var failures = [];                  // machine-readable trail for specs / console sweeps

  // Was the defining <script> even emitted, and how? This distinguishes the three real causes:
  // tag absent (Liquid never rendered it) / tag present but global undefined (delivery or
  // execution fault) / duplicate tags (double include).
  function tagState(asset) {
    var found = 0;
    var first = null;
    for (var i = 0; i < document.scripts.length; i++) {
      if (String(document.scripts[i].src).indexOf(asset) !== -1) {
        found++;
        if (!first) first = document.scripts[i];
      }
    }
    if (!found) return 'tag:ABSENT';
    return 'tag:present' + (found > 1 ? '(x' + found + ')' : '') +
      ' defer:' + !!first.defer + ' async:' + !!first.async;
  }

  function report(subject, context, asset, tag) {
    var key = context + '|' + subject;
    if (reported[key]) return;
    reported[key] = 1;
    failures.push({
      missing: subject,
      context: context,
      asset: asset || null,
      tag: tag || null,
      readyState: document.readyState,
      at: Date.now()
    });
    console.error(
      '[km2-deps] ' + context + ': window.' + subject + ' missing — ' +
      (asset ? asset + ' defined nothing. ' : '') +
      (tag ? tag + ' ' : '') +
      'readyState:' + document.readyState
    );
  }

  /** The global, or null having logged exactly why it is absent. */
  function get(globalName, context) {
    var v = window[globalName];
    if (v) return v;
    var asset = ASSETS[globalName];
    report(globalName, context || '?', asset, asset ? tagState(asset) : null);
    return null;
  }

  /**
   * The global, but only when `member` is callable on it — the check consumers actually want
   * (KM2Brands.createProductPager, KM2ProductCard.quickAddButtonHTML). A present global with a
   * missing member is its own failure mode and gets its own message.
   */
  function need(globalName, member, context) {
    var v = get(globalName, context);
    if (!v) return null;
    if (member && typeof v[member] !== 'function') {
      report(globalName + '.' + member, context || '?', ASSETS[globalName],
        'global present, member missing');
      return null;
    }
    return v;
  }

  /*
   * Run `fn` once the DOM is parsed AND every deferred script has executed.
   *
   * This replaces the idiom that was copy-pasted into ten files:
   *
   *     if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
   *     else init();                                   // <-- WRONG
   *
   * document.readyState has THREE values, and that `else` collapses two of them:
   *
   *     'loading'      parsing; deferred scripts pending; DOMContentLoaded ahead.
   *     'interactive'  parsing DONE, but DEFERRED SCRIPTS HAVE NOT RUN YET; DOMContentLoaded ahead.
   *     'complete'     load finished; everything has run.
   *
   * So at 'interactive' the old code called init() SYNCHRONOUSLY, before the deferred assets that
   * define window.KM2Brands / KM2BrandTiles / KM2Rail had executed — the consumer saw an undefined
   * global and (until km2-deps) silently rendered an empty rail. Only 'complete' is safe to run
   * immediately; 'interactive' must still wait for DOMContentLoaded, which fires AFTER deferred
   * scripts. That single distinction is the whole bug.
   */
  function ready(fn) {
    if (document.readyState === 'complete') { fn(); return; }
    document.addEventListener('DOMContentLoaded', fn, { once: true });
  }

  /*
   * Memoise a value ONLY on success — the fix for the poisoned-cache class of bug.
   *
   * Consumers wrote this:
   *     if (pager === undefined) pager = dep ? dep.make() : null;   // caches the FAILURE
   *     if (!pager) return [];
   *
   * A single call that lands before the deferred data layer has executed writes `null`
   * PERMANENTLY. The dependency then arrives milliseconds later, but nothing ever asks again, so
   * the rail stays blank for the entire life of the page. That is precisely how a working EBC
   * rail rendered nothing while its own module reported "ASSIGNED ok".
   *
   * memo() caches a truthy result and RETRIES while `create()` yields nothing, so a transient
   * load-order miss self-heals on the next call instead of becoming permanent.
   */
  function memo(create) {
    var value = null;
    return function () {
      if (value) return value;
      value = create() || null;
      return value;
    };
  }

  /*
   * Resolve a global that may not have loaded YET — WAIT for it instead of concluding it is absent.
   *
   * ready() + memo() together still leave one hole: a consumer that asks once, gets nothing, and
   * reports an empty result to a caller that treats empty as "the feed is exhausted" and never
   * asks again. The dependency then arrives, but no one is listening. Returning a promise lets the
   * consumer await the dependency instead of racing it.
   *
   * Resolves with the global once `member` is callable on it, or with null after `timeoutMs`
   * (reporting ONCE at that point — a transient miss must not spam the console).
   */
  function whenAvailable(globalName, member, context, timeoutMs) {
    var limit = typeof timeoutMs === 'number' ? timeoutMs : 10000;
    var step = 50;
    return new Promise(function (resolve) {
      var waited = 0;
      (function poll() {
        var v = window[globalName];
        if (v && (!member || typeof v[member] === 'function')) return resolve(v);
        if (waited >= limit) { need(globalName, member, context); return resolve(null); }
        waited += step;
        setTimeout(poll, step);
      })();
    });
  }


  /*
   * Read a JSON ARRAY out of localStorage — the shape-safe replacement for the
   * `JSON.parse(localStorage.getItem(k)) || []` idiom.
   *
   * That idiom guards null but NOT type. Any syntactically valid JSON that is not an array
   * (an object, number, string, bool) parses cleanly, sails past `|| []`, and then throws on
   * the first `.indexOf`/`.filter`/`.slice` the caller does — OUTSIDE the try/catch, because
   * the parse already succeeded. An independent crawl (2026-09-07) hit exactly this in
   * recently-viewed: `{"foo":"bar"}` in the history key threw `h.indexOf is not a function`,
   * aborted the section's inline IIFE, and left a heading over an empty wrapper on EVERY page
   * load thereafter. It never self-heals, because the bad value is never overwritten.
   *
   * Wrong-shape is treated exactly like unparseable: return [] and let the caller re-seed.
   * Storage being unavailable at all (private mode, blocked cookies) throws on getItem and is
   * caught the same way — a read must never be able to break a page.
   */
  function readArray(key) {
    try {
      var parsed = JSON.parse(localStorage.getItem(key));
      return Array.isArray(parsed) ? parsed : [];
    } catch (e) {
      return [];
    }
  }

  window.KM2Deps = {
    get: get,
    need: need,
    ready: ready,
    memo: memo,
    whenAvailable: whenAvailable,
    readArray: readArray,
    failures: failures, // specs assert: expect(KM2Deps.failures).toEqual([])
    assets: ASSETS
  };
})();
